import { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  getBillingBatches, getBillingRawRows, getBlinkitRiderMeta,
  getBlinkitPayoutRows, upsertBlinkitRiderMeta, upsertBlinkitPayoutRow,
  getAllBlinkitRiderMeta, deleteBlinkitRiderMeta,
} from '../lib/supabase';

// ─── Theme ──────────────────────────────────────────────────────────────────
const T = {
  bg: '#0d1117', card: '#0f1923', border: '#1e2433',
  text: '#e2e8f0', muted: '#6b7280', accent: '#f59e0b',
  blue: '#1e3a5f', blueText: '#60a5fa',
  green: '#14532d', greenText: '#4ade80',
  red: '#7f1d1d', redText: '#f87171',
  rowEven: '#0d1117', rowOdd: '#111927',
  headerBg: '#111827', headerText: '#9ca3af',
};

// ─── Shared helpers ───────────────────────────────────────────────────────────
function r2(n) { return n == null ? 0 : Math.round(Number(n) * 100) / 100; }
function fmtNum(n) { if (n == null || n === '') return ''; return Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtInt(n) { if (n == null || n === '') return ''; return Math.round(Number(n)).toLocaleString('en-IN'); }

const NUM_FIELDS = [
  'total_earnings','tds_deduction','non_tds_deduction','missing_item_penalty',
  'support_fee_deduction','settlement_adjustment','retention_bonus','referral_bonus',
  'tip_amount','pocket_withdrawal','manual_additions','manual_deductions','login_hours',
  'surge_pay','base_pay','bonus_pay','daily_milestone','distance_pay','incentive_pay',
  'daily_ming','trip_ming','ldrp','weekly_milestone','weekly_ming','offer_incentive',
  'current_offer_pay','accounting_orders','acc_orders_distance','delivered_orders',
  'delivered_orders_distance','weight_pay','COD','grosspay','netpay_to_rider',
];

function aggregateBatch(rows) {
  const byDriver = {};
  for (const item of rows) {
    const r = item.raw_data;
    if (!r || !r.driver_id) continue;
    const id = r.driver_id;
    if (!byDriver[id]) {
      byDriver[id] = {
        week: r.week, driver_id: r.driver_id,
        rider_name: r.rider_name || '', runnr_city: r.runnr_city || '',
        itr_filing_fees: null, payout_blocker: r.payout_blocker || '',
        _stores: {},
      };
      for (const f of NUM_FIELDS) byDriver[id][f] = 0;
    }
    const agg = byDriver[id];
    const sid = String(r.blinkit_store_id || '');
    if (sid) {
      if (!agg._stores[sid]) agg._stores[sid] = { name: r.blinkit_store_name || '', orders: 0 };
      agg._stores[sid].orders += Number(r.accounting_orders || 0);
    }
    if (r.itr_filing_fees != null) agg.itr_filing_fees = r2((agg.itr_filing_fees || 0) + Number(r.itr_filing_fees));
    for (const f of NUM_FIELDS) {
      const v = r[f];
      if (v != null && v !== '') agg[f] = r2(agg[f] + Number(v));
    }
  }
  return Object.values(byDriver).map(agg => {
    const entries = Object.entries(agg._stores);
    const [topSid, topSdata] = entries.length > 0
      ? entries.sort((a, b) => b[1].orders - a[1].orders)[0]
      : ['', { name: '' }];
    const { _stores, ...rest } = agg;
    return { ...rest, blinkit_store_id: topSid, blinkit_store_name: topSdata.name };
  }).sort((a, b) =>
    (a.runnr_city || '').localeCompare(b.runnr_city || '') ||
    (a.rider_name || '').localeCompare(b.rider_name || '')
  );
}

function calcFinal(row, prow) {
  const net   = Number(row?.netpay_to_rider || 0);
  const ev    = Number(prow?.ev_rent || 0);
  const neg   = Number(prow?.negative || 0);
  const dep   = Number(prow?.deposit || 0);
  const wav   = Number(prow?.waveoff || 0);
  const oth   = Number(prow?.other_deduction || 0);
  const drec  = Number(prow?.deposit_recovery || 0);
  const spare = Number(prow?.spare_penalty || 0);
  const adv   = Number(prow?.advance || 0);
  return r2(net - ev - neg - dep - oth - spare - adv + wav + drec);
}

// ─── Shared input styles ─────────────────────────────────────────────────────
const inputSt = {
  background: '#0d1117', border: '1px solid #1e2433', borderRadius: 4,
  color: '#e2e8f0', padding: '5px 8px', fontSize: 12, width: '100%',
  outline: 'none', fontFamily: 'inherit',
};
const labelSt  = { color: '#9ca3af', fontSize: 11, marginBottom: 2, display: 'block' };
const secSt    = { color: '#f59e0b', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 8, marginTop: 4 };
const thSt     = { padding: '8px 10px', color: '#60a5fa', fontWeight: 700, fontSize: 11, letterSpacing: 0.5, whiteSpace: 'nowrap', borderBottom: '1px solid #1e2433', background: '#1e3a5f', textAlign: 'left' };

function FieldInput({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label style={labelSt}>{label}</label>
      <input type={type} value={value || ''} onChange={onChange} style={inputSt} />
    </div>
  );
}

// ─── Rider Detail Modal (payout + account) ────────────────────────────────────
function RiderDetailModal({ row, meta, prow, onSave, onClose }) {
  const [m, setM] = useState({ ...meta });
  const [p, setP] = useState({ ...prow });
  const finalPay  = calcFinal(row, p);

  const fm = f => e => setM(prev => ({ ...prev, [f]: e.target.value }));
  const fp = f => e => setP(prev => ({ ...prev, [f]: e.target.value }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000090', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#0f1923', border: '1px solid #1e2433', borderRadius: 12, padding: 24, width: 640, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 14 }}>{row.rider_name}</div>
            <div style={{ color: '#6b7280', fontSize: 11 }}>{row.driver_id} · {row.runnr_city} · Net Pay: ₹{fmtNum(row.netpay_to_rider)}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={secSt}>VEHICLE / EV DETAILS</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
          <FieldInput label="Bike No"      value={m.bike_no}           onChange={fm('bike_no')} />
          <FieldInput label="Onboarding"   value={m.onboarding_date}   onChange={fm('onboarding_date')} />
          <FieldInput label="Offboarding"  value={m.offboarding_date}  onChange={fm('offboarding_date')} />
        </div>

        <div style={secSt}>PAYOUT ADJUSTMENTS</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
          <FieldInput label="EV Days"           value={p.ev_days}           onChange={fp('ev_days')} type="number" />
          <FieldInput label="Ev Rent (₹)"       value={p.ev_rent}           onChange={fp('ev_rent')} type="number" />
          <FieldInput label="Waveoff (₹)"       value={p.waveoff}           onChange={fp('waveoff')} type="number" />
          <FieldInput label="Negative (₹)"      value={p.negative}          onChange={fp('negative')} type="number" />
          <FieldInput label="Deposit (₹)"       value={p.deposit}           onChange={fp('deposit')} type="number" />
          <FieldInput label="Deposit Recovery"  value={p.deposit_recovery}  onChange={fp('deposit_recovery')} type="number" />
          <FieldInput label="Spare Penalty (₹)" value={p.spare_penalty}     onChange={fp('spare_penalty')} type="number" />
          <FieldInput label="Advance (₹)"       value={p.advance}           onChange={fp('advance')} type="number" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 16 }}>
          <FieldInput label="Other Deduction (₹)" value={p.other_deduction} onChange={fp('other_deduction')} type="number" />
          <FieldInput label="Reason"               value={p.deduction_reason} onChange={fp('deduction_reason')} />
        </div>

        {/* Final pay summary */}
        <div style={{ background: '#172032', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div><span style={{ color: '#9ca3af', fontSize: 11 }}>Net Pay (Blinkit)</span><br /><strong style={{ color: '#60a5fa', fontSize: 15 }}>₹{fmtNum(row.netpay_to_rider)}</strong></div>
          <div><span style={{ color: '#9ca3af', fontSize: 11 }}>EV Rent</span><br /><strong style={{ color: '#f87171', fontSize: 15 }}>- ₹{fmtNum(p.ev_rent || 0)}</strong></div>
          <div><span style={{ color: '#9ca3af', fontSize: 11 }}>Final Payment</span><br /><strong style={{ color: finalPay >= 0 ? '#4ade80' : '#f87171', fontSize: 15 }}>₹{fmtNum(finalPay)}</strong></div>
        </div>

        <div style={secSt}>ACCOUNT DETAILS <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 10 }}>— saved to Master Riders</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <FieldInput label="A/C Holder Name" value={m.acct_holder}  onChange={fm('acct_holder')} />
          <FieldInput label="A/C No."         value={m.acct_number}  onChange={fm('acct_number')} />
          <FieldInput label="Bank Name"        value={m.bank_name}   onChange={fm('bank_name')} />
          <FieldInput label="IFSC Code"        value={m.ifsc_code}   onChange={fm('ifsc_code')} />
          <FieldInput label="PAN Card"         value={m.pan_no}      onChange={fm('pan_no')} />
        </div>

        <div style={secSt}>PAYMENT STATUS</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 20 }}>
          <div>
            <label style={labelSt}>Pmt Status</label>
            <select value={p.pmt_status || ''} onChange={fp('pmt_status')} style={inputSt}>
              <option value="">—</option>
              <option value="Done">Done</option>
              <option value="Pending">Pending</option>
              <option value="On Hold">On Hold</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <FieldInput label="Remarks" value={p.remarks} onChange={fp('remarks')} />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', background: '#1e2433', border: '1px solid #2d3748', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          <button onClick={() => onSave(m, p)} style={{ padding: '8px 18px', background: '#1e3a5f', border: 'none', borderRadius: 6, color: '#60a5fa', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Master Rider Edit Modal (account details only) ──────────────────────────
function MasterRiderModal({ rider, onSave, onClose }) {
  const isNew = !rider.driver_id;
  const [r, setR] = useState({
    driver_id: '', rider_name: '', bike_no: '',
    onboarding_date: '', offboarding_date: '',
    acct_holder: '', acct_number: '', bank_name: '',
    ifsc_code: '', pan_no: '',
    ...rider,
  });
  const f = field => e => setR(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000090', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#0f1923', border: '1px solid #1e2433', borderRadius: 12, padding: 24, width: 580, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 14 }}>{isNew ? 'Add New Rider' : `Edit: ${r.rider_name || r.driver_id}`}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={secSt}>RIDER IDENTITY</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <FieldInput label="Driver ID *"  value={r.driver_id}  onChange={f('driver_id')} />
          <FieldInput label="Rider Name"   value={r.rider_name} onChange={f('rider_name')} />
        </div>

        <div style={secSt}>VEHICLE</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
          <FieldInput label="Bike No"      value={r.bike_no}           onChange={f('bike_no')} />
          <FieldInput label="Onboarding"   value={r.onboarding_date}   onChange={f('onboarding_date')} />
          <FieldInput label="Offboarding"  value={r.offboarding_date}  onChange={f('offboarding_date')} />
        </div>

        <div style={secSt}>BANK ACCOUNT DETAILS</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
          <FieldInput label="A/C Holder Name" value={r.acct_holder}  onChange={f('acct_holder')} />
          <FieldInput label="A/C No."         value={r.acct_number}  onChange={f('acct_number')} />
          <FieldInput label="Bank Name"        value={r.bank_name}   onChange={f('bank_name')} />
          <FieldInput label="IFSC Code"        value={r.ifsc_code}   onChange={f('ifsc_code')} />
          <FieldInput label="PAN Card"         value={r.pan_no}      onChange={f('pan_no')} />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', background: '#1e2433', border: '1px solid #2d3748', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          <button
            onClick={() => { if (!r.driver_id.trim()) { alert('Driver ID is required'); return; } onSave(r); }}
            style={{ padding: '8px 18px', background: '#14532d', border: 'none', borderRadius: 6, color: '#4ade80', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            {isNew ? '+ Add Rider' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Master Riders Tab ────────────────────────────────────────────────────────
function MasterRidersTab() {
  const [riders, setRiders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [editRider, setEditRider]   = useState(null);   // null | rider object | {}
  const [confirmDel, setConfirmDel] = useState(null);   // driver_id to delete
  const [saving, setSaving]         = useState(false);

  async function loadRiders() {
    setLoading(true);
    try { setRiders(await getAllBlinkitRiderMeta()); }
    catch (e) { console.error(e); alert('Failed to load master riders: ' + e.message); }
    setLoading(false);
  }

  useEffect(() => { loadRiders(); }, []);

  async function handleSave(r) {
    setSaving(true);
    try {
      await upsertBlinkitRiderMeta(r);
      await loadRiders();
      setEditRider(null);
    } catch (e) { alert('Save failed: ' + e.message); }
    setSaving(false);
  }

  async function handleDelete(driverId) {
    try {
      await deleteBlinkitRiderMeta(driverId);
      setRiders(prev => prev.filter(r => r.driver_id !== driverId));
      setConfirmDel(null);
    } catch (e) { alert('Delete failed: ' + e.message); }
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return riders;
    const q = search.trim().toLowerCase();
    return riders.filter(r =>
      (r.driver_id || '').toLowerCase().includes(q) ||
      (r.rider_name || '').toLowerCase().includes(q) ||
      (r.acct_number || '').toLowerCase().includes(q) ||
      (r.bank_name || '').toLowerCase().includes(q)
    );
  }, [riders, search]);

  const tdSt = (i, col) => ({
    padding: '7px 10px', fontSize: 12,
    color: col === 'id' ? T.muted : col === 'name' ? '#d1d5db' : T.text,
    fontWeight: col === 'name' ? 600 : 400,
    borderBottom: '1px solid #161d2a',
    background: i % 2 === 0 ? T.rowEven : T.rowOdd,
    whiteSpace: 'nowrap',
  });

  const acctComplete = r => !!(r.acct_number && r.bank_name && r.ifsc_code);

  return (
    <div style={{ padding: 20, color: T.text }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: T.accent, fontWeight: 800, fontSize: 14, letterSpacing: 1 }}>MASTER RIDERS</div>
          <div style={{ color: T.muted, fontSize: 11 }}>Account details saved here are auto-populated in all payout sheets</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <input
            placeholder="Search by name / ID / account…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, padding: '6px 12px', fontSize: 12, width: 240, outline: 'none' }} />
          <button onClick={() => setEditRider({})}
            style={{ padding: '7px 14px', background: T.green, border: 'none', borderRadius: 7, color: T.greenText, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            + Add Rider
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        {[
          [`${riders.length}`, 'Total riders'],
          [`${riders.filter(acctComplete).length}`, 'With account details'],
          [`${riders.filter(r => r.bike_no).length}`, 'With bike assigned'],
        ].map(([val, lbl]) => (
          <div key={lbl} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 14px' }}>
            <div style={{ color: T.blueText, fontWeight: 700, fontSize: 16 }}>{val}</div>
            <div style={{ color: T.muted, fontSize: 10 }}>{lbl}</div>
          </div>
        ))}
        <div style={{ color: T.muted, fontSize: 11, alignSelf: 'center', marginLeft: 4 }}>
          {filtered.length !== riders.length && `Showing ${filtered.length} of ${riders.length}`}
        </div>
      </div>

      {loading ? (
        <div style={{ color: T.muted, fontSize: 12, padding: 40, textAlign: 'center' }}>Loading master riders…</div>
      ) : riders.length === 0 ? (
        <div style={{ color: T.muted, fontSize: 12, padding: 40, textAlign: 'center' }}>
          No riders in master data yet.<br />
          <span style={{ fontSize: 11 }}>Account details are added automatically when you save them in a payout sheet, or click "+ Add Rider".</span>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${T.border}` }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                {['Driver ID','Rider Name','Bike No','A/C Holder','A/C Number','Bank','IFSC','PAN','Account','Actions'].map(h => (
                  <th key={h} style={thSt}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.driver_id}>
                  <td style={tdSt(i, 'id')}>{r.driver_id}</td>
                  <td style={tdSt(i, 'name')}>{r.rider_name || '—'}</td>
                  <td style={tdSt(i)}>{r.bike_no || '—'}</td>
                  <td style={tdSt(i)}>{r.acct_holder || '—'}</td>
                  <td style={tdSt(i)}>{r.acct_number || '—'}</td>
                  <td style={tdSt(i)}>{r.bank_name || '—'}</td>
                  <td style={tdSt(i)}>{r.ifsc_code || '—'}</td>
                  <td style={tdSt(i)}>{r.pan_no || '—'}</td>
                  <td style={tdSt(i)}>
                    {acctComplete(r)
                      ? <span style={{ color: '#4ade80', fontSize: 11 }}>✓ Complete</span>
                      : <span style={{ color: '#f87171', fontSize: 11 }}>⚠ Incomplete</span>}
                  </td>
                  <td style={{ ...tdSt(i), padding: '4px 8px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setEditRider(r)}
                        style={{ background: '#1e2433', border: `1px solid ${T.border}`, borderRadius: 5, color: T.muted, cursor: 'pointer', fontSize: 11, padding: '3px 9px' }}>
                        ✏ Edit
                      </button>
                      <button onClick={() => setConfirmDel(r.driver_id)}
                        style={{ background: '#7f1d1d', border: 'none', borderRadius: 5, color: '#f87171', cursor: 'pointer', fontSize: 11, padding: '3px 9px' }}>
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / Add modal */}
      {editRider !== null && (
        <MasterRiderModal
          rider={editRider}
          onSave={handleSave}
          onClose={() => setEditRider(null)}
        />
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <div style={{ position: 'fixed', inset: 0, background: '#00000090', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0f1923', border: '1px solid #7f1d1d', borderRadius: 12, padding: 24, width: 360 }}>
            <div style={{ color: '#f87171', fontWeight: 700, marginBottom: 8 }}>Delete Rider?</div>
            <div style={{ color: T.muted, fontSize: 12, marginBottom: 20 }}>
              Remove <strong style={{ color: T.text }}>{confirmDel}</strong> from master data?<br />
              This only removes the saved account details — payout history is unaffected.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDel(null)} style={{ padding: '8px 16px', background: '#1e2433', border: '1px solid #2d3748', borderRadius: 6, color: T.muted, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
              <button onClick={() => handleDelete(confirmDel)} style={{ padding: '8px 16px', background: '#7f1d1d', border: 'none', borderRadius: 6, color: '#f87171', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Payout Sheets Tab ────────────────────────────────────────────────────────
function PayoutSheetsTab() {
  const [batches, setBatches]           = useState([]);
  const [batchLoading, setBatchLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [aggregated, setAggregated]    = useState([]);
  const [metaMap, setMetaMap]          = useState({});
  const [prowMap, setProwMap]          = useState({});
  const [editMeta, setEditMeta]        = useState({});
  const [editProw, setEditProw]        = useState({});
  const [dirtyMeta, setDirtyMeta]      = useState(new Set());
  const [dirtyProw, setDirtyProw]      = useState(new Set());
  const [saving, setSaving]            = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [search, setSearch]            = useState('');
  const [cityFilter, setCityFilter]    = useState('All');

  const getMeta = useCallback(id => ({ ...(metaMap[id] || {}), ...(editMeta[id] || {}) }), [metaMap, editMeta]);
  const getProw = useCallback(id => ({ ...(prowMap[id] || {}), ...(editProw[id] || {}) }), [prowMap, editProw]);

  useEffect(() => {
    getBillingBatches()
      .then(data => { setBatches(data); setBatchLoading(false); })
      .catch(e => { console.error(e); setBatchLoading(false); });
  }, []);

  async function loadSheet(batch) {
    setSheetLoading(true);
    setAggregated([]); setMetaMap({}); setProwMap({}); setEditMeta({}); setEditProw({});
    setDirtyMeta(new Set()); setDirtyProw(new Set());
    try {
      const rawRows = await getBillingRawRows(batch.id);
      const agg = aggregateBatch(rawRows);
      setAggregated(agg);
      const ids = agg.map(r => r.driver_id);
      const [metaRows, prowRows] = await Promise.all([
        getBlinkitRiderMeta(ids),
        getBlinkitPayoutRows(batch.id),
      ]);
      setMetaMap(Object.fromEntries(metaRows.map(m => [m.driver_id, m])));
      setProwMap(Object.fromEntries(prowRows.map(p => [p.driver_id, p])));
    } catch (e) {
      console.error(e);
      alert('Error loading billing data: ' + e.message);
    }
    setSheetLoading(false);
  }

  function handleBatchSelect(batch) {
    setSelectedBatch(batch);
    setCityFilter('All');
    setSearch('');
    loadSheet(batch);
  }

  function handleModalSave(driverId, newMeta, newProw) {
    setEditMeta(prev => ({ ...prev, [driverId]: newMeta }));
    setEditProw(prev => ({ ...prev, [driverId]: newProw }));
    setDirtyMeta(prev => new Set([...prev, driverId]));
    setDirtyProw(prev => new Set([...prev, driverId]));
    setEditingDriver(null);
  }

  async function saveAll() {
    if (!selectedBatch) return;
    setSaving(true);
    try {
      const metaUpserts = [...dirtyMeta].map(id => ({
        driver_id: id,
        rider_name: aggregated.find(r => r.driver_id === id)?.rider_name || '',
        ...(metaMap[id] || {}),
        ...(editMeta[id] || {}),
      }));
      const prowUpserts = [...dirtyProw].map(id => ({
        batch_id: selectedBatch.id,
        driver_id: id,
        ...(prowMap[id] || {}),
        ...(editProw[id] || {}),
      }));
      await Promise.all([
        ...metaUpserts.map(m => upsertBlinkitRiderMeta(m)),
        ...prowUpserts.map(p => upsertBlinkitPayoutRow(p)),
      ]);
      await loadSheet(selectedBatch);
      alert(`Saved ${metaUpserts.length + prowUpserts.length} records.`);
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
    setSaving(false);
  }

  async function exportExcel() {
    if (!selectedBatch || aggregated.length === 0) return;
    const HEADER = [
      'week','driver_id','rider_name','runnr_city','blinkit_store_id','blinkit_store_name',
      'itr_filing_fees','payout_blocker','total_earnings','tds_deduction','non_tds_deduction',
      'missing_item_penalty','support_fee_deduction','settlement_adjustment','retention_bonus',
      'referral_bonus','tip_amount','pocket_withdrawal','manual_additions','manual_deductions',
      'login_hours','surge_pay','base_pay','bonus_pay','daily_milestone','distance_pay',
      'incentive_pay','daily_ming','trip_ming','ldrp','weekly_milestone','weekly_ming',
      'offer_incentive','current_offer_pay','accounting_orders','acc_orders_distance',
      'delivered_orders','delivered_orders_distance','weight_pay','COD','grosspay',
      'netpay_to_rider','Bike No','Onboarding','Offboarding','Negative','Deposit',
      'EV Days','Ev rent','Waveoff','Other deduction','Reason','Deposit Recovery',
      'Spare Penalty','Advance','Final Payment','A/C Holder Name','A/C No.',
      'Bank Name','IFSC Code','Pan Card','Pmt Status','Remarks',
    ];
    const nv = v => (v == null || v === 0 || v === '') ? '' : v;
    const rows = aggregated.map(row => {
      const meta = getMeta(row.driver_id);
      const prow = getProw(row.driver_id);
      const finalPay = calcFinal(row, prow);
      const noAdj = !prow.ev_rent && !prow.negative && !prow.deposit && !prow.other_deduction && !prow.spare_penalty && !prow.advance;
      return [
        row.week, row.driver_id, row.rider_name, row.runnr_city,
        row.blinkit_store_id, row.blinkit_store_name,
        row.itr_filing_fees == null ? '(blank)' : row.itr_filing_fees,
        row.payout_blocker,
        r2(row.total_earnings),          r2(row.tds_deduction),
        nv(r2(row.non_tds_deduction)),   nv(r2(row.missing_item_penalty)),
        r2(row.support_fee_deduction),   r2(row.settlement_adjustment),
        r2(row.retention_bonus),         nv(r2(row.referral_bonus)),
        nv(r2(row.tip_amount)),          nv(r2(row.pocket_withdrawal)),
        r2(row.manual_additions),        r2(row.manual_deductions),
        r2(row.login_hours),             r2(row.surge_pay),
        r2(row.base_pay),                r2(row.bonus_pay),
        r2(row.daily_milestone),         r2(row.distance_pay),
        r2(row.incentive_pay),           r2(row.daily_ming),
        r2(row.trip_ming),               r2(row.ldrp),
        r2(row.weekly_milestone),        r2(row.weekly_ming),
        r2(row.offer_incentive),         r2(row.current_offer_pay),
        row.accounting_orders,           r2(row.acc_orders_distance),
        row.delivered_orders,            r2(row.delivered_orders_distance),
        r2(row.weight_pay),              nv(r2(row.COD)),
        r2(row.grosspay),                r2(row.netpay_to_rider),
        meta.bike_no || '',
        meta.onboarding_date || '',
        meta.offboarding_date || '',
        nv(prow.negative),    nv(prow.deposit),
        nv(prow.ev_days),     nv(prow.ev_rent),
        nv(prow.waveoff),     nv(prow.other_deduction),
        prow.deduction_reason || '',
        nv(prow.deposit_recovery), nv(prow.spare_penalty),
        nv(prow.advance),
        noAdj ? r2(row.netpay_to_rider) : finalPay,
        meta.acct_holder || '', meta.acct_number || '',
        meta.bank_name || '',   meta.ifsc_code || '',
        meta.pan_no || '',      prow.pmt_status || '',
        prow.remarks || '',
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([HEADER, ...rows]);
    ws['!cols'] = HEADER.map((h, i) => ({ wch: i < 2 ? 6 : i < 6 ? 20 : i < 8 ? 12 : 10 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, selectedBatch.period || 'Payout');
    XLSX.writeFile(wb, `Blinkit_Payout_${selectedBatch.period || 'sheet'}.xlsx`);
  }

  const cities = useMemo(() =>
    ['All', ...Array.from(new Set(aggregated.map(r => r.runnr_city).filter(Boolean))).sort()],
    [aggregated]
  );

  const filtered = useMemo(() => {
    let rows = aggregated;
    if (cityFilter !== 'All') rows = rows.filter(r => r.runnr_city === cityFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.rider_name || '').toLowerCase().includes(q) ||
        (r.driver_id || '').toLowerCase().includes(q) ||
        (r.blinkit_store_name || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [aggregated, cityFilter, search]);

  const totals = useMemo(() => ({
    riders: filtered.length,
    orders: filtered.reduce((s, r) => s + Number(r.accounting_orders || 0), 0),
    grosspay: r2(filtered.reduce((s, r) => s + Number(r.grosspay || 0), 0)),
    netpay: r2(filtered.reduce((s, r) => s + Number(r.netpay_to_rider || 0), 0)),
    evrent: r2(filtered.reduce((s, r) => s + Number(getProw(r.driver_id).ev_rent || 0), 0)),
    finalPay: r2(filtered.reduce((s, r) => s + calcFinal(r, getProw(r.driver_id)), 0)),
  }), [filtered, editProw, prowMap]); // eslint-disable-line

  const hasDirty = dirtyMeta.size > 0 || dirtyProw.size > 0;
  const cardSt   = { background: T.card, borderRadius: 10, border: `1px solid ${T.border}`, padding: '12px 18px', flex: 1 };

  // ── Batch list ──────────────────────────────────────────────────────────────
  if (!selectedBatch) {
    return (
      <div style={{ padding: 24, color: T.text }}>
        <div style={{ color: T.accent, fontWeight: 800, fontSize: 16, letterSpacing: 2, marginBottom: 4 }}>PAYOUT SHEETS</div>
        <div style={{ color: T.muted, fontSize: 12, marginBottom: 20 }}>Select a billing batch to generate its payout sheet</div>
        {batchLoading ? (
          <div style={{ color: T.muted, fontSize: 12 }}>Loading batches…</div>
        ) : batches.length === 0 ? (
          <div style={{ color: T.muted, fontSize: 12 }}>No billing batches found.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 700 }}>
            {batches.map(b => (
              <div key={b.id} onClick={() => handleBatchSelect(b)}
                style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                <div>
                  <div style={{ color: T.text, fontWeight: 700, fontSize: 13 }}>{b.filename}</div>
                  <div style={{ color: T.muted, fontSize: 11, marginTop: 3 }}>
                    <span style={{ color: T.blueText }}>{b.client_name}</span>
                    {' · '}{b.period}
                    {' · '}{(b.row_count || 0).toLocaleString()} rows
                    {' · '}{new Date(b.uploaded_at).toLocaleDateString('en-IN')}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: T.greenText, fontWeight: 700, fontSize: 14 }}>₹{Number(b.total_payout || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                  <div style={{ color: T.muted, fontSize: 10 }}>total payout</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Payout sheet ────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 20, color: T.text }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setSelectedBatch(null)}
          style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, color: T.muted, cursor: 'pointer', fontSize: 12, padding: '5px 10px' }}>
          ← Back
        </button>
        <div>
          <div style={{ color: T.text, fontWeight: 700, fontSize: 14 }}>{selectedBatch.filename}</div>
          <div style={{ color: T.muted, fontSize: 11 }}>{selectedBatch.client_name} · {selectedBatch.period} · {selectedBatch.row_count} billing rows</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {hasDirty && (
            <button onClick={saveAll} disabled={saving}
              style={{ padding: '7px 14px', background: '#1e3a5f', border: 'none', borderRadius: 7, color: '#60a5fa', fontWeight: 700, fontSize: 12, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : `💾 Save (${dirtyMeta.size + dirtyProw.size} changed)`}
            </button>
          )}
          <button onClick={exportExcel} disabled={aggregated.length === 0}
            style={{ padding: '7px 14px', background: T.green, border: 'none', borderRadius: 7, color: T.greenText, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            ⬇ Export Excel
          </button>
        </div>
      </div>

      {sheetLoading ? (
        <div style={{ color: T.muted, fontSize: 12, padding: 40, textAlign: 'center' }}>Aggregating {selectedBatch.row_count} rows…</div>
      ) : (
        <>
          {/* Cards */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              ['RIDERS', fmtInt(totals.riders), T.blueText],
              ['ORDERS', fmtInt(totals.orders), '#a78bfa'],
              ['GROSS PAY', '₹' + fmtNum(totals.grosspay), T.text],
              ['NET PAY (Blinkit)', '₹' + fmtNum(totals.netpay), T.text],
              ['EV RENT', '₹' + fmtNum(totals.evrent), '#f87171'],
              ['FINAL PAY', '₹' + fmtNum(totals.finalPay), T.greenText],
            ].map(([label, val, color]) => (
              <div key={label} style={cardSt}>
                <div style={{ color: T.muted, fontSize: 10, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>{label}</div>
                <div style={{ color, fontSize: 16, fontWeight: 700 }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <input placeholder="Search rider / driver ID / store…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, padding: '6px 12px', fontSize: 12, width: 260, outline: 'none' }} />
            <select value={cityFilter} onChange={e => setCityFilter(e.target.value)}
              style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, padding: '6px 10px', fontSize: 12, outline: 'none' }}>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{ color: T.muted, fontSize: 11 }}>{filtered.length} of {aggregated.length} riders</div>
            <div style={{ color: T.muted, fontSize: 11, marginLeft: 8 }}>
              {aggregated.filter(r => metaMap[r.driver_id]?.acct_number).length} / {aggregated.length} have master account data
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${T.border}` }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
              <thead>
                <tr>
                  {['City','Rider Name','Driver ID','Store','Orders','Hrs','Net Pay (₹)','EV Rent (₹)','Final Pay (₹)','Bike','Account','Status','Remarks',''].map(h => (
                    <th key={h} style={thSt}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => {
                  const meta = getMeta(row.driver_id);
                  const prow = getProw(row.driver_id);
                  const finalPay = calcFinal(row, prow);
                  const noAdj = !prow.ev_rent && !prow.negative && !prow.deposit && !prow.other_deduction;
                  const isDone = (prow.pmt_status || '').toLowerCase().startsWith('done');
                  const hasMasterAcct = !!(metaMap[row.driver_id]?.acct_number);
                  const rowBg = i % 2 === 0 ? T.rowEven : T.rowOdd;
                  const tdBase = { padding: '7px 10px', fontSize: 12, color: T.text, borderBottom: '1px solid #161d2a', background: rowBg, whiteSpace: 'nowrap' };
                  return (
                    <tr key={row.driver_id}>
                      <td style={tdBase}>{row.runnr_city}</td>
                      <td style={{ ...tdBase, fontWeight: 600, color: '#d1d5db' }}>{row.rider_name}</td>
                      <td style={{ ...tdBase, color: T.muted, fontSize: 11 }}>{row.driver_id}</td>
                      <td style={{ ...tdBase, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.blinkit_store_name}>{row.blinkit_store_name}</td>
                      <td style={{ ...tdBase, textAlign: 'right' }}>{row.accounting_orders}</td>
                      <td style={{ ...tdBase, textAlign: 'right' }}>{r2(row.login_hours)}</td>
                      <td style={{ ...tdBase, textAlign: 'right', color: '#60a5fa', fontWeight: 600 }}>{fmtNum(row.netpay_to_rider)}</td>
                      <td style={{ ...tdBase, padding: '4px 6px' }}>
                        <input type="number" min="0" step="1"
                          value={prow.ev_rent || ''}
                          onChange={e => {
                            const v = e.target.value;
                            setEditProw(prev => ({ ...prev, [row.driver_id]: { ...(prev[row.driver_id] || {}), ev_rent: v } }));
                            setDirtyProw(prev => new Set([...prev, row.driver_id]));
                          }}
                          style={{ background: '#172032', border: `1px solid ${T.border}`, borderRadius: 4, color: '#f87171', padding: '4px 7px', fontSize: 12, width: 90, textAlign: 'right', outline: 'none', fontFamily: 'inherit' }}
                          placeholder="0" />
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: (noAdj ? row.netpay_to_rider : finalPay) < 0 ? '#f87171' : T.greenText }}>
                        {fmtNum(noAdj ? row.netpay_to_rider : finalPay)}
                      </td>
                      <td style={{ ...tdBase, color: T.muted, fontSize: 11 }}>{meta.bike_no || '—'}</td>
                      <td style={tdBase}>
                        {hasMasterAcct
                          ? <span style={{ color: T.greenText, fontSize: 11 }}>✓ Master</span>
                          : meta.acct_number
                          ? <span style={{ color: '#facc15', fontSize: 11 }}>~ Unsaved</span>
                          : <span style={{ color: T.muted, fontSize: 11 }}>—</span>}
                      </td>
                      <td style={{ ...tdBase, padding: '4px 6px' }}>
                        <select value={prow.pmt_status || ''}
                          onChange={e => {
                            const v = e.target.value;
                            setEditProw(prev => ({ ...prev, [row.driver_id]: { ...(prev[row.driver_id] || {}), pmt_status: v } }));
                            setDirtyProw(prev => new Set([...prev, row.driver_id]));
                          }}
                          style={{ background: isDone ? '#14532d' : '#1e2433', border: `1px solid ${T.border}`, borderRadius: 4, color: isDone ? T.greenText : T.muted, padding: '4px 7px', fontSize: 11, outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}>
                          <option value="">—</option>
                          <option value="Done">Done</option>
                          <option value="Pending">Pending</option>
                          <option value="On Hold">On Hold</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>
                      <td style={{ ...tdBase, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11, color: T.muted }} title={prow.remarks}>{prow.remarks || ''}</td>
                      <td style={{ ...tdBase, padding: '4px 8px' }}>
                        <button onClick={() => setEditingDriver(row.driver_id)} title="Edit all details"
                          style={{ background: '#1e2433', border: `1px solid ${T.border}`, borderRadius: 5, color: T.muted, cursor: 'pointer', fontSize: 11, padding: '3px 9px' }}>✏</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr>
                    {Array.from({ length: 14 }, (_, i) => (
                      <td key={i} style={{ padding: '8px 10px', borderTop: `2px solid ${T.border}`, background: '#111827', color: T.accent, fontWeight: 700, fontSize: 12, textAlign: i >= 4 ? 'right' : 'left' }}>
                        {i === 1 ? 'TOTAL'
                          : i === 4 ? fmtInt(totals.orders)
                          : i === 6 ? fmtNum(totals.netpay)
                          : i === 7 ? fmtNum(totals.evrent)
                          : i === 8 ? fmtNum(totals.finalPay)
                          : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {/* Detail edit modal */}
      {editingDriver && (() => {
        const row = aggregated.find(r => r.driver_id === editingDriver);
        if (!row) return null;
        return (
          <RiderDetailModal
            row={row}
            meta={getMeta(editingDriver)}
            prow={getProw(editingDriver)}
            onSave={(newMeta, newProw) => handleModalSave(editingDriver, newMeta, newProw)}
            onClose={() => setEditingDriver(null)}
          />
        );
      })()}
    </div>
  );
}

// ─── Root BillingSection ──────────────────────────────────────────────────────
export default function BillingSection() {
  const [tab, setTab] = useState('payouts'); // 'payouts' | 'master'

  const tabBtnSt = (active, color) => ({
    padding: '7px 16px', border: 'none', borderRadius: 8, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, transition: 'all .15s',
    background: active ? (color === 'green' ? T.green : T.blue) : 'transparent',
    color: active ? (color === 'green' ? T.greenText : T.blueText) : T.muted,
    boxShadow: active ? '0 1px 4px #00000060' : 'none',
  });

  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      {/* Sub-nav */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: '10px 20px', display: 'flex', gap: 4, background: '#080c12' }}>
        <div style={{ display: 'flex', background: '#0b0e14', border: `1px solid ${T.border}`, borderRadius: 10, padding: 3, gap: 2 }}>
          <button onClick={() => setTab('payouts')} style={tabBtnSt(tab === 'payouts', 'blue')}>
            📄 Payout Sheets
          </button>
          <button onClick={() => setTab('master')} style={tabBtnSt(tab === 'master', 'green')}>
            👥 Master Riders
          </button>
        </div>
      </div>

      {tab === 'payouts' && <PayoutSheetsTab />}
      {tab === 'master' && <MasterRidersTab />}
    </div>
  );
}
